import { Trans, useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import InputText from '@/design_system/InputText';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconTrash from '@/icons/Trash.svg';
import { I18nWording } from '@/services/i18n';

import { LocaleInputs } from '../shared/LocaleInputs';

import { QuestionType } from './ConditionFormEditor';

export const getDefaultConfig = (type: QuestionType) => {
  switch (type) {
    case 'multi-quantifiable-select':
      return {
        addButton: { label: { fr: '', en: '' } },
        select: { placeholder: { fr: '', en: '' } },
      };
    case 'text-and-select':
      return {
        input: { label: { fr: '', en: '' }, placeholder: { fr: '', en: '' } },
        select: { placeholder: { fr: '', en: '' }, options: [] },
      };
    default:
      return null;
  }
};

export const QuestionConfigEditor = ({
  config,
  type,
  onConfigChange,
  showErrors,
}: {
  config: any;
  type: QuestionType;
  onConfigChange: (config: any) => void;
  showErrors: boolean;
}) => {
  if (type === 'multi-quantifiable-select') {
    return (
      <MultiQuantifiableSelectConfig
        config={config}
        onConfigChange={onConfigChange}
        showErrors={showErrors}
      />
    );
  }
  if (type === 'text-and-select') {
    return (
      <TextAndSelectConfig
        config={config}
        onConfigChange={onConfigChange}
        showErrors={showErrors}
      />
    );
  }

  return null;
};

const MultiQuantifiableSelectConfig = ({
  config,
  onConfigChange,
  showErrors,
}: {
  config: any;
  onConfigChange: (config: any) => void;
  showErrors: boolean;
}) => {
  const { t } = useLingui();

  const currentConfig = config ?? {
    addButton: { label: { fr: '', en: '' } },
    select: { placeholder: { fr: '', en: '' } },
  };

  return (
    <Box>
      <Stack gap="0.75rem">
        <Title size="400" weight="bold">
          <Trans id="settings.condition-forms.config">Configuration</Trans>
        </Title>
        <LocaleInputs
          label={t({
            id: 'settings.condition-forms.config.add-button-label',
            message: 'Add button label',
          })}
          placeholder={t({
            id: 'settings.condition-forms.config.add-button-label',
            message: 'Add button label',
          })}
          value={currentConfig.addButton?.label ?? { fr: '', en: '' }}
          setValue={(label) =>
            onConfigChange({
              ...currentConfig,
              addButton: { ...currentConfig.addButton, label },
            })
          }
          showErrors={showErrors}
        />
        <LocaleInputs
          label={t({
            id: 'settings.condition-forms.config.select-placeholder',
            message: 'Select placeholder',
          })}
          placeholder={t({
            id: 'settings.condition-forms.config.select-placeholder',
            message: 'Select placeholder',
          })}
          value={currentConfig.select?.placeholder ?? { fr: '', en: '' }}
          setValue={(placeholder) =>
            onConfigChange({
              ...currentConfig,
              select: { ...currentConfig.select, placeholder },
            })
          }
          showErrors={showErrors}
        />
      </Stack>
    </Box>
  );
};

const TextAndSelectConfig = ({
  config,
  onConfigChange,
  showErrors,
}: {
  config: any;
  onConfigChange: (config: any) => void;
  showErrors: boolean;
}) => {
  const { t, i18n } = useLingui();

  const currentConfig = config ?? {
    input: { label: { fr: '', en: '' }, placeholder: { fr: '', en: '' } },
    select: { placeholder: { fr: '', en: '' }, options: [] },
  };
  const options: { label: I18nWording; value: string }[] = currentConfig.select?.options ?? [];

  return (
    <Box>
      <Stack gap="0.75rem">
        <Title size="400" weight="bold">
          <Trans id="settings.condition-forms.config">Configuration</Trans>
        </Title>
        <LocaleInputs
          label={t({
            id: 'settings.condition-forms.config.input-label',
            message: 'Input label',
          })}
          placeholder={t({
            id: 'settings.condition-forms.config.input-label',
            message: 'Input label',
          })}
          value={currentConfig.input?.label ?? { fr: '', en: '' }}
          setValue={(label) =>
            onConfigChange({
              ...currentConfig,
              input: { ...currentConfig.input, label },
            })
          }
          showErrors={showErrors}
        />
        <LocaleInputs
          label={t({
            id: 'settings.condition-forms.config.input-placeholder',
            message: 'Input placeholder',
          })}
          placeholder={t({
            id: 'settings.condition-forms.config.input-placeholder',
            message: 'Input placeholder',
          })}
          value={currentConfig.input?.placeholder ?? { fr: '', en: '' }}
          setValue={(placeholder) =>
            onConfigChange({
              ...currentConfig,
              input: { ...currentConfig.input, placeholder },
            })
          }
          showErrors={showErrors}
        />
        <LocaleInputs
          label={t({
            id: 'settings.condition-forms.config.select-placeholder',
            message: 'Select placeholder',
          })}
          placeholder={t({
            id: 'settings.condition-forms.config.select-placeholder',
            message: 'Select placeholder',
          })}
          value={currentConfig.select?.placeholder ?? { fr: '', en: '' }}
          setValue={(placeholder) =>
            onConfigChange({
              ...currentConfig,
              select: { ...currentConfig.select, placeholder },
            })
          }
          showErrors={showErrors}
        />
        <Stack gap="0.5rem">
          <Title size="400" weight="bold">
            <Trans id="settings.condition-forms.config.select-options">Select options</Trans>
          </Title>
          {options.map((option, optionIndex) => (
            <Box key={optionIndex}>
              <Stack gap="0.5rem">
                <Stack row alignItems="center" justifyContent="space-between">
                  <Paragraph size="200" weight="medium">
                    {i18n._({
                      id: 'settings.condition-forms.config.option',
                      message: 'Option {order}',
                      values: { order: optionIndex + 1 },
                    })}
                  </Paragraph>
                  <Button
                    iconOnly
                    variant="secondary-danger"
                    size="small"
                    onPress={() => {
                      const newOptions = options.filter((_, i) => i !== optionIndex);

                      onConfigChange({
                        ...currentConfig,
                        select: { ...currentConfig.select, options: newOptions },
                      });
                    }}
                    ariaLabel={t({
                      id: 'settings.condition-forms.config.delete-option',
                      message: 'Delete option',
                    })}
                  >
                    <IconTrash />
                  </Button>
                </Stack>
                <LocaleInputs
                  label={t({
                    id: 'settings.condition-forms.config.option-label',
                    message: 'Option label',
                  })}
                  placeholder={t({
                    id: 'settings.condition-forms.config.option-label',
                    message: 'Option label',
                  })}
                  value={option.label}
                  setValue={(label) => {
                    const newOptions = [...options];

                    newOptions[optionIndex] = { ...newOptions[optionIndex], label };
                    onConfigChange({
                      ...currentConfig,
                      select: { ...currentConfig.select, options: newOptions },
                    });
                  }}
                  showErrors={showErrors}
                />
                <InputText
                  label={t({
                    id: 'settings.condition-forms.config.option-value',
                    message: 'Option value',
                  })}
                  placeholder={t({
                    id: 'settings.condition-forms.config.option-value',
                    message: 'Option value',
                  })}
                  value={option.value}
                  onChange={(value) => {
                    const newOptions = [...options];

                    newOptions[optionIndex] = { ...newOptions[optionIndex], value };
                    onConfigChange({
                      ...currentConfig,
                      select: { ...currentConfig.select, options: newOptions },
                    });
                  }}
                  isInvalid={showErrors && !option.value}
                />
              </Stack>
            </Box>
          ))}
          {options.length === 0 && (
            <Paragraph size="200" color="secondary">
              <Trans id="settings.condition-forms.config.no-options">
                No options yet. Add at least one option.
              </Trans>
            </Paragraph>
          )}
          <Button
            variant="secondary"
            size="small"
            onPress={() => {
              const newOptions = [...options, { label: { fr: '', en: '' }, value: '' }];

              onConfigChange({
                ...currentConfig,
                select: { ...currentConfig.select, options: newOptions },
              });
            }}
          >
            <IconAdd />
            <Trans id="settings.condition-forms.config.add-option">Add option</Trans>
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};
